import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Crown } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { getPlans, createCheckout } from '../../utils/api';
import './PricingPlans.css';

interface Plan {
  id: string;
  name: string;
  tagline: string;
  monthlyPrice: number;
  yearlyPrice: number;
  color: string;
  popular: boolean;
  features: string[];
  paidFeatures: string[];
}

type BillingPeriod = 'monthly' | 'yearly';

const PLAN_RANK: Record<string, number> = { free: 0, storyteller: 1, chronicle: 2, friends_family: 2 };

interface PricingPlansProps {
  mode: 'landing' | 'authenticated';
}

export default function PricingPlans({ mode }: PricingPlansProps) {
  const { user } = useAuth();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  const userRank = PLAN_RANK[user?.plan || 'free'] ?? 0;

  useEffect(() => {
    getPlans()
      .then((res) => setPlans(res.data.plans))
      .catch(() => toast.error('Could not load plans'))
      .finally(() => setLoading(false));
  }, []);

  const handleUpgrade = async (planId: string) => {
    if (planId === 'free' || planId === user?.plan) return;
    setLoadingPlan(planId);
    try {
      const res = await createCheckout(planId, billingPeriod);
      window.location.href = res.data.url;
    } catch {
      toast.error('Could not start checkout');
    } finally {
      setLoadingPlan(null);
    }
  };

  const getDisplayPrice = (plan: Plan) => {
    if (plan.monthlyPrice === 0) return { main: 'Free', sub: 'forever', amount: 0 };
    if (billingPeriod === 'yearly') {
      const monthlyEquiv = (plan.yearlyPrice / 12).toFixed(2);
      return {
        main: `$${monthlyEquiv}`,
        sub: '/month',
        amount: plan.yearlyPrice,
        annual: `Billed $${plan.yearlyPrice}/year`,
      };
    }
    return { main: `$${plan.monthlyPrice}`, sub: '/month', amount: plan.monthlyPrice };
  };

  const getYearlySavings = (plan: Plan) => {
    if (plan.monthlyPrice === 0) return 0;
    return Math.round((1 - plan.yearlyPrice / (plan.monthlyPrice * 12)) * 100);
  };

  const getButtonLabel = (plan: Plan) => {
    if (mode === 'landing') {
      if (plan.id === 'free') return 'Begin Creating';
      if (plan.id === 'storyteller') return 'Begin Your Tale';
      return 'Unlock the Chronicle';
    }
    // authenticated mode
    const planRank = PLAN_RANK[plan.id] ?? 0;
    if (user?.plan === plan.id) return 'Current Plan';
    if (loadingPlan === plan.id) return 'Loading...';
    if (plan.monthlyPrice === 0) return 'Get Started';
    if (planRank > userRank) return 'Upgrade Now';
    return null; // hide for downgrades
  };

  const getButtonAction = (plan: Plan) => {
    if (mode === 'landing') {
      return plan.monthlyPrice === 0 ? '/create' : '/signup';
    }
    return null; // handled by onClick
  };

  // Calculate max savings across paid plans for the toggle badge
  const maxSavings = plans
    .filter((p) => p.monthlyPrice > 0)
    .reduce((max, p) => Math.max(max, getYearlySavings(p)), 0);

  if (loading) {
    return (
      <div className="pricing-plans-loading">
        <div className="pricing-plans-grid">
          {[1, 2, 3].map((i) => (
            <div key={i} className="pricing-plans-skeleton" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="pricing-plans">
      {mode === 'authenticated' && user?.plan === 'friends_family' && (
        <div className="ff-plan-banner">
          You're on the <strong>Friends &amp; Family</strong> plan — all Chronicle features included.
        </div>
      )}
      {/* Billing period toggle */}
      <div className="pricing-plans-toggle">
        <button
          className={billingPeriod === 'monthly' ? 'active' : ''}
          onClick={() => setBillingPeriod('monthly')}
        >
          Monthly
        </button>
        <button
          className={billingPeriod === 'yearly' ? 'active' : ''}
          onClick={() => setBillingPeriod('yearly')}
        >
          Yearly
          {maxSavings > 0 && <span className="savings-badge">Save {maxSavings}%</span>}
        </button>
      </div>

      {/* Plan cards */}
      <div className="pricing-plans-grid">
        {plans.map((plan, i) => {
          const price = getDisplayPrice(plan);
          const buttonLabel = getButtonLabel(plan);
          const linkTo = getButtonAction(plan);
          const isCurrentPlan = mode === 'authenticated' && user?.plan === plan.id;
          const isPaid = (f: string) => plan.paidFeatures?.includes(f);

          return (
            <motion.div
              key={plan.id}
              className={`pricing-plans-card ${plan.popular ? 'popular' : ''} ${isCurrentPlan ? 'current' : ''}`}
              style={{ backgroundColor: `var(--${plan.color})` }}
              initial={{ opacity: 0, y: 20 }}
              {...(mode === 'landing'
                ? { whileInView: { opacity: 1, y: 0 }, viewport: { once: true } }
                : { animate: { opacity: 1, y: 0 } }
              )}
              transition={{ delay: i * 0.1 }}
              whileHover={mode === 'landing' ? { y: -5 } : undefined}
            >
              {plan.popular && !isCurrentPlan && <div className="plans-badge popular-badge">Most Popular</div>}
              {isCurrentPlan && <div className="plans-badge current-badge">Current Plan</div>}

              <h3 className="plans-name">{plan.name}</h3>
              <p className="plans-tagline">{plan.tagline}</p>

              <div className="plans-price">
                <span className="plans-price-amount">{price.main}</span>
                {price.sub !== 'forever' && <span className="plans-price-period">{price.sub}</span>}
                {price.sub === 'forever' && <span className="plans-price-period">{price.sub}</span>}
              </div>
              {(price as any).annual && (
                <p className="plans-annual">{(price as any).annual}</p>
              )}

              <ul className="plans-features">
                {plan.features.map((f, j) => (
                  <li key={j}>
                    {isPaid(f) ? <Crown size={18} fill="currentColor" className="crown-icon" /> : <Check size={16} />}
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {buttonLabel && (
                mode === 'landing' && linkTo ? (
                  <Link
                    to={linkTo}
                    className={`btn ${plan.popular ? 'btn-primary' : 'btn-secondary'} plans-cta`}
                  >
                    {buttonLabel}
                  </Link>
                ) : (
                  <button
                    className={`btn ${plan.popular ? 'btn-primary' : 'btn-secondary'} plans-cta`}
                    onClick={() => handleUpgrade(plan.id)}
                    disabled={isCurrentPlan || loadingPlan === plan.id}
                  >
                    {buttonLabel}
                  </button>
                )
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
